import { useMemo } from 'react';

import { doc, DocumentReference } from 'firebase/firestore';

import { useDocumentData } from 'react-firebase-hooks/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import Grouping from '../types/Grouping';

interface UseDivisionProps {
  orgId?: string | null;
  divisionId?: string | null;
}

export default function useDivision(props: UseDivisionProps) {
  const { orgId, divisionId } = props;
  const { isGod } = useAuth();
  const { userState, isOrgActive } = useData();

  const divisionRef = useMemo(() => {
    if (!orgId || !divisionId) return null;
    return doc(firestore, 'org', orgId, 'division', divisionId) as DocumentReference<Grouping>;
  }, [orgId, divisionId]);

  const [division, isDivisionLoading, divisionError] = useDocumentData<Grouping>(divisionRef);

  if (userState.status === 'logged_out') {
    return {
      division: null,
      isDivisionLoading: false,
      divisionError: new Error('User must be logged in to view divisions') as Error | null,
    };
  }

  if (
    userState.status === 'ready' &&
    userState.userProfile &&
    !isDivisionLoading &&
    !divisionError &&
    !isGod &&
    orgId &&
    !isOrgActive(orgId)
  ) {
    return {
      division: null,
      isDivisionLoading: false,
      divisionError: new Error('Not authorized to view this division') as Error | null,
    };
  }

  return {
    division: division ?? null,
    isDivisionLoading:
      isDivisionLoading ||
      userState.status === 'authenticating_user' ||
      userState.status === 'authenticating_profile' ||
      userState.status === 'authenticating_memberships',
    divisionError: divisionError as Error | null,
  };
}
